import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import Select from "components/Shared/TranslatedSelect"
import { Link } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { assignOrdersToRider, getOrderDetails } from "store/actions/orderAction"
import { showAlert } from "store/reducers/alertReducer"
import { post } from "helpers/api_helper"
import { RIDERS_FOR_ASSIGNMENT } from "helpers/url_helper"
import { getRiderAvailabilityDisplay } from "utils/riderAvailability"
import { getSystemStatusDisplay } from "utils/orderStatusUtils"
import formatDisplayDateTime from "components/Shared/formatDateTime"
import LtrText from "components/Shared/LtrText"
import {
  formatOrderAddress,
  getOrderBranchName,
  getOrderCustomerName,
  getOrderPhone,
} from "utils/mapMarkerUtils"

const riderProgressLabel = (status, t) => {
  if (!status) return "-"
  const labels = {
    Assigned: t("Assigned"),
    Accepted: t("Accepted"),
    ArrivedPickup: t("Arrived at Branch"),
    PickedUp: t("Picked Up"),
    OutForDelivery: t("Out for Delivery"),
    ArrivedDrop: t("Arrived at Customer"),
    Delivered: t("Delivered"),
    Failed: t("Failed"),
    Cancelled: t("Cancelled"),
  }
  return labels[status] || status
}

const deliveryStatusLabel = (status, t) => {
  const labels = {
    unassigned: t("Not Assigned"),
    assigned: t("Assigned"),
    in_progress: t("In Progress"),
    delivered: t("Delivered"),
    cancelled: t("Cancelled"),
  }
  return labels[status] || status || "-"
}

const OrderMapDetailModal = ({
  isOpen,
  toggle,
  order,
  branchsList = [],
  canAssign = false,
  ordersBasePath = "/orders",
  onSuccess,
  showEstimatedDeliveryDate = false,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()

  const orderId = order?._id ? String(order._id) : ""

  const [loadingDetails, setLoadingDetails] = useState(false)
  const [orderDetails, setOrderDetails] = useState(null)
  const [branchFilter, setBranchFilter] = useState(null)
  const [availabilityFilter, setAvailabilityFilter] = useState(null)
  const [riders, setRiders] = useState([])
  const [loadingRiders, setLoadingRiders] = useState(false)
  const [selectedRider, setSelectedRider] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const defaultBranchId = useMemo(() => {
    const branchRef = orderDetails?.branchId || order?.branchId
    if (!branchRef) return ""
    return typeof branchRef === "object" ? branchRef._id : branchRef
  }, [orderDetails, order])

  const branchOptions = useMemo(
    () => [
      { label: t("All Riders"), value: "all" },
      ...branchsList.map(branch => ({
        label: language === "sa" ? branch.nameAr : branch.name,
        value: branch._id,
      })),
    ],
    [branchsList, language, t]
  )

  const availabilityOptions = useMemo(
    () => [
      { label: t("All"), value: "all" },
      { label: t("Available"), value: "available" },
      { label: t("Not Available"), value: "unavailable" },
      { label: t("On Delivery"), value: "on_delivery" },
    ],
    [t]
  )

  const displayOrder = orderDetails || order

  const assignedRider = useMemo(() => {
    const partner = displayOrder?.deliveryPartnerId
    if (partner && typeof partner === "object") return partner
    if (displayOrder?.deliveryPartnerName) {
      return { name: displayOrder.deliveryPartnerName }
    }
    return null
  }, [displayOrder])

  const isAssigned = Boolean(
    assignedRider?._id ||
      (displayOrder?.deliveryPartnerId &&
        typeof displayOrder.deliveryPartnerId !== "object") ||
      displayOrder?.deliveryPartnerName ||
      (displayOrder?.deliveryStatus &&
        displayOrder.deliveryStatus !== "unassigned")
  )

  useEffect(() => {
    if (!isOpen || !orderId) return

    setLoadingDetails(true)
    setOrderDetails(null)
    dispatch(getOrderDetails(orderId))
      .then(action => {
        const payload = action?.payload
        if (payload?.status && payload?.data) {
          setOrderDetails(payload.data)
        } else {
          setOrderDetails(order)
        }
      })
      .catch(() => setOrderDetails(order))
      .finally(() => setLoadingDetails(false))
  }, [isOpen, orderId, dispatch, order])

  useEffect(() => {
    if (!isOpen || isAssigned) return

    const defaultOption =
      branchOptions.find(o => String(o.value) === String(defaultBranchId)) ||
      branchOptions.find(o => o.value === "all") ||
      null
    setBranchFilter(defaultOption)
    setAvailabilityFilter(availabilityOptions[0])
    setSelectedRider(null)
  }, [
    isOpen,
    isAssigned,
    defaultBranchId,
    branchOptions,
    availabilityOptions,
  ])

  useEffect(() => {
    if (!isOpen || isAssigned || !branchFilter || !availabilityFilter) return

    const fetchRiders = async () => {
      setLoadingRiders(true)
      try {
        const response = await post(RIDERS_FOR_ASSIGNMENT, {
          branchId: branchFilter.value,
          availabilityFilter: availabilityFilter.value,
        })
        setRiders(response?.data || [])
      } catch {
        setRiders([])
      } finally {
        setLoadingRiders(false)
      }
    }

    fetchRiders()
  }, [isOpen, isAssigned, branchFilter, availabilityFilter])

  const handleAssign = () => {
    if (!selectedRider || !orderId) {
      dispatch(showAlert({ text: t("Please select a rider"), type: "error" }))
      return
    }

    setSubmitting(true)
    dispatch(
      assignOrdersToRider({
        orderIds: [orderId],
        partnerId: selectedRider._id,
      })
    )
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          onSuccess?.()
          toggle()
        } else {
          dispatch(
            showAlert({ text: msg || t("Something went wrong"), type: "error" })
          )
        }
      })
      .finally(() => setSubmitting(false))
  }

  const statusDisplay = getSystemStatusDisplay(displayOrder, t)
  const customerName = getOrderCustomerName(displayOrder, language)
  const customerPhone = getOrderPhone(displayOrder)
  const branchName = getOrderBranchName(displayOrder, language)

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader toggle={toggle}>
        {t("Order")} #{displayOrder?.invoiceNo || "-"}
      </ModalHeader>
      <ModalBody>
        {loadingDetails ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted mb-0">{t("Loading order details...")}</p>
          </div>
        ) : (
          <>
            <Row className="g-3 mb-4">
              <Col md={6}>
                <div className="small text-muted">{t("Customer")}</div>
                <div className="fw-semibold">{customerName || "-"}</div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">{t("Phone Number")}</div>
                <div>
                  {customerPhone ? (
                    <LtrText>{customerPhone}</LtrText>
                  ) : (
                    "-"
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">{t("Branch Name")}</div>
                <div>{branchName || "-"}</div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">{t("Date & Time")}</div>
                <div>
                  {displayOrder?.createdAt
                    ? formatDisplayDateTime(displayOrder.createdAt)
                    : "-"}
                </div>
              </Col>
              {showEstimatedDeliveryDate ? (
                <Col md={6}>
                  <div className="small text-muted">
                    {t("Estimated Delivery Date")}
                  </div>
                  <div>
                    {displayOrder?.orderType === "delivery" &&
                    displayOrder?.estimatedDeliveryDate
                      ? formatDisplayDateTime(
                          displayOrder.estimatedDeliveryDate
                        )
                      : "-"}
                  </div>
                </Col>
              ) : null}
              <Col md={12}>
                <div className="small text-muted">{t("Delivery Address")}</div>
                <div>{formatOrderAddress(displayOrder, language) || "-"}</div>
              </Col>
              {displayOrder?.notes ? (
                <Col md={12}>
                  <div className="small text-muted">{t("Notes")}</div>
                  <div className="text-break">{displayOrder.notes}</div>
                </Col>
              ) : null}
              <Col md={6}>
                <div className="small text-muted">{t("Status")}</div>
                <div
                  className="fw-semibold"
                  style={{ color: statusDisplay?.color || "#9B9B9B" }}
                >
                  {statusDisplay?.text || "-"}
                </div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">{t("Rider Assignment")}</div>
                <div>{deliveryStatusLabel(displayOrder?.deliveryStatus, t)}</div>
              </Col>
            </Row>

            {isAssigned ? (
              <div className="border rounded p-3 bg-light">
                <h6 className="mb-3">{t("Assigned Rider")}</h6>
                <Table className="table-sm mb-0">
                  <tbody>
                    <tr>
                      <th scope="row">{t("Rider")}</th>
                      <td>
                        {assignedRider?._id ? (
                          <Link
                            to={`/delivery-partners/${assignedRider._id}`}
                            className="text-primary"
                          >
                            {assignedRider.name}
                          </Link>
                        ) : (
                          assignedRider?.name || "-"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">{t("Rider Status")}</th>
                      <td>
                        {riderProgressLabel(displayOrder?.status, t) ||
                          deliveryStatusLabel(displayOrder?.deliveryStatus, t)}
                      </td>
                    </tr>
                    {displayOrder?.assignedToRiderAt && (
                      <tr>
                        <th scope="row">{t("Assigned At")}</th>
                        <td>
                          {formatDisplayDateTime(displayOrder.assignedToRiderAt)}
                        </td>
                      </tr>
                    )}
                    {displayOrder?.acceptedAt && (
                      <tr>
                        <th scope="row">{t("Accepted At")}</th>
                        <td>{formatDisplayDateTime(displayOrder.acceptedAt)}</td>
                      </tr>
                    )}
                    {displayOrder?.pickedUpAt && (
                      <tr>
                        <th scope="row">{t("Picked Up At")}</th>
                        <td>{formatDisplayDateTime(displayOrder.pickedUpAt)}</td>
                      </tr>
                    )}
                    {displayOrder?.deliveredAt && (
                      <tr>
                        <th scope="row">{t("Delivered At")}</th>
                        <td>{formatDisplayDateTime(displayOrder.deliveredAt)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            ) : canAssign ? (
              <div className="border rounded p-3">
                <h6 className="mb-3">{t("Assign to Rider")}</h6>
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <label className="form-label">{t("Filter by Branch")}</label>
                    <Select
                      value={branchFilter}
                      options={branchOptions}
                      onChange={setBranchFilter}
                      placeholder={t("Select Branch")}
                    />
                  </Col>
                  <Col md={6}>
                    <label className="form-label">
                      {t("Filter by Availability")}
                    </label>
                    <Select
                      value={availabilityFilter}
                      options={availabilityOptions}
                      onChange={setAvailabilityFilter}
                      placeholder={t("Select Availability")}
                    />
                  </Col>
                </Row>

                {loadingRiders ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" /> {t("Loading riders...")}
                  </div>
                ) : riders.length === 0 ? (
                  <p className="text-muted mb-0">{t("No riders found")}</p>
                ) : (
                  <Row className="g-2">
                    {riders.map(rider => {
                      const isSelected = selectedRider?._id === rider._id
                      const availability = getRiderAvailabilityDisplay(rider, t)
                      return (
                        <Col md={6} key={rider._id}>
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedRider(rider)}
                            onKeyDown={e =>
                              e.key === "Enter" && setSelectedRider(rider)
                            }
                            className={`border rounded p-2 ${
                              isSelected ? "border-primary bg-light" : ""
                            }`}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="rounded-circle flex-shrink-0"
                                style={{
                                  width: 8,
                                  height: 8,
                                  backgroundColor: availability.color,
                                }}
                              />
                              <span className="fw-semibold small">
                                {rider.name}
                              </span>
                            </div>
                            <div className="small text-muted ms-3">
                              {availability.riderLabel}
                            </div>
                          </div>
                        </Col>
                      )
                    })}
                  </Row>
                )}
              </div>
            ) : (
              <p className="text-muted mb-0">{t("Not Assigned")}</p>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <Link
          to={`${ordersBasePath}/order-details?id=${orderId}`}
          className="btn btn-link"
          onClick={toggle}
        >
          {t("Open Full Details")}
        </Link>
        <div className="d-flex gap-2">
          <Button color="secondary" onClick={toggle} disabled={submitting}>
            {t("Close")}
          </Button>
          {!isAssigned && canAssign && (
            <Button
              color="primary"
              onClick={handleAssign}
              disabled={submitting || !selectedRider || loadingDetails}
            >
              {submitting ? <Spinner size="sm" /> : t("Assign")}
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default OrderMapDetailModal
